import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, ExternalLink, Plane, Hotel, Car, Sparkles,
  BookmarkPlus, Loader2, TrendingDown,
} from 'lucide-react'
import { useTrip } from '../trips/useTrip'
import {
  searchMarketplace,
  compareMarketplace,
  getMarketplaceRecommendations,
  saveMarketplaceOffer,
  type MarketplaceCategory,
} from '../../shared/api/marketplace'
import { Button } from '../../shared/components/Button'
import { Spinner } from '../../shared/components/Spinner'
import { cn } from '../../shared/lib/cn'
import type { MarketplaceOffer } from '../../shared/api/types'

const CATEGORIES: { id: MarketplaceCategory; label: string; Icon: typeof Plane }[] = [
  { id: 'flight', label: 'Flights', Icon: Plane },
  { id: 'stay', label: 'Stays', Icon: Hotel },
  { id: 'car', label: 'Car rental', Icon: Car },
]

export function MarketplacePage() {
  const { id } = useParams<{ id: string }>()
  const tripId = id as string
  const trip = useTrip(id)
  const qc = useQueryClient()

  const [category, setCategory] = useState<MarketplaceCategory>('flight')
  const [origin, setOrigin] = useState('TLV')
  const [compareMode, setCompareMode] = useState(false)
  const [savedId, setSavedId] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  const recs = useQuery({
    queryKey: ['marketplace-recs', tripId],
    queryFn: () => getMarketplaceRecommendations(tripId),
  })

  const search = useQuery({
    queryKey: ['marketplace', tripId, category, compareMode, origin],
    queryFn: () =>
      compareMode
        ? compareMarketplace(tripId, category)
        : searchMarketplace(tripId, category, {
            origin,
            destination: trip.data?.destination,
            depart_date: trip.data?.start_date ?? undefined,
            return_date: trip.data?.end_date ?? undefined,
          }),
    enabled: Boolean(trip.data),
  })

  const saveMut = useMutation({
    mutationFn: (offer: MarketplaceOffer) =>
      saveMarketplaceOffer(tripId, {
        title: offer.title,
        url: offer.url,
        affiliate_url: offer.affiliate_url,
        provider: offer.provider,
        price: offer.price,
        currency: offer.currency,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['links', tripId] })
      setToast('Saved to Links')
      setTimeout(() => setToast(null), 3000)
    },
  })

  return (
    <div className="mx-auto max-w-2xl pb-12">
      <Link
        to={`/trips/${tripId}`}
        className="mb-4 inline-flex items-center gap-1 text-sm text-st-muted hover:text-st-text"
      >
        <ArrowLeft className="h-4 w-4" /> Back to overview
      </Link>

      <div className="mb-5 flex items-center gap-2">
        <span className="flex h-10 w-10 items-center justify-center rounded-st-md bg-st-primary/10 text-st-primary">
          <TrendingDown className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl font-bold text-st-text">Marketplace</h1>
          <p className="text-sm text-st-muted">{trip.data?.title} — compare & save deals</p>
        </div>
      </div>

      {toast && (
        <p className="mb-3 rounded-st-md bg-st-primary/10 px-3 py-2 text-sm text-st-primary">{toast}</p>
      )}

      {recs.data && recs.data.length > 0 && (
        <section className="mb-5 rounded-st-lg border border-st-line bg-st-surface p-4">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-st-text">
            <Sparkles className="h-4 w-4 text-st-primary" /> Smart suggestions
          </h2>
          <div className="space-y-2">
            {recs.data.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setCategory(r.category)}
                className="w-full rounded-st-md bg-st-surface-2 px-3 py-2 text-left transition hover:bg-st-primary/5"
              >
                <p className="text-sm font-medium text-st-text">{r.title}</p>
                <p className="text-xs text-st-muted">{r.reason}</p>
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        {CATEGORIES.map(({ id: cat, label, Icon }) => (
          <button
            key={cat}
            type="button"
            onClick={() => { setCategory(cat); setCompareMode(false) }}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-st-full px-3 py-1.5 text-xs font-medium transition',
              category === cat ? 'bg-st-primary text-white' : 'bg-st-surface-2 text-st-muted hover:text-st-text',
            )}
          >
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>

      {category === 'flight' && (
        <div className="mb-4 flex gap-2">
          <label className="flex-1 text-xs text-st-muted">
            Origin
            <input
              value={origin}
              onChange={(e) => setOrigin(e.target.value.toUpperCase())}
              className="mt-1 w-full rounded-st-md border border-st-line bg-st-surface px-3 py-2 text-sm text-st-text"
              placeholder="TLV"
            />
          </label>
        </div>
      )}

      <div className="mb-4 flex gap-2">
        <Button variant="accent" onClick={() => { setCompareMode(false); void search.refetch() }} disabled={search.isFetching}>
          {search.isFetching ? <Spinner label="Searching…" /> : 'Search deals'}
        </Button>
        <Button variant="outline" onClick={() => { setCompareMode(true); void search.refetch() }} disabled={search.isFetching}>
          Compare prices
        </Button>
      </div>

      {search.isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-st-muted" />
        </div>
      )}

      {search.data && search.data.offers.length > 0 && (
        <div className="space-y-3">
          {search.data.cheapest_price != null && (
            <p className="text-xs text-st-muted">
              From {search.data.currency} {search.data.cheapest_price.toLocaleString()} · {search.data.offers.length} offers
            </p>
          )}
          {search.data.offers.map((offer) => (
            <OfferCard
              key={offer.id}
              offer={offer}
              saving={saveMut.isPending && savedId === offer.id}
              onSave={() => {
                setSavedId(offer.id)
                saveMut.mutate(offer)
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function OfferCard({
  offer,
  saving,
  onSave,
}: {
  offer: MarketplaceOffer
  saving: boolean
  onSave: () => void
}) {
  return (
    <div className="rounded-st-lg border border-st-line bg-st-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-st-text">{offer.title}</p>
          <p className="text-xs text-st-muted">{offer.description}</p>
          <p className="mt-1 text-lg font-bold text-st-text">
            {offer.currency} {offer.price.toLocaleString()}
          </p>
        </div>
        {offer.badge && (
          <span className={cn(
            'shrink-0 rounded-st-full px-2 py-0.5 text-xs font-medium',
            offer.is_best_price ? 'bg-green-100 text-green-800' : 'bg-st-surface-2 text-st-muted',
          )}>
            {offer.badge}
          </span>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <a
          href={offer.affiliate_url || offer.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-st-md bg-st-primary px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
        >
          <ExternalLink className="h-3.5 w-3.5" /> Open deal
        </a>
        <Button variant="ghost" className="text-xs" onClick={onSave} disabled={saving}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BookmarkPlus className="h-3.5 w-3.5" />}
          Save to Links
        </Button>
      </div>
    </div>
  )
}

export default MarketplacePage
